import { HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy, signal } from '@angular/core';
import {
  ChangeRequesterPasswordGQL,
  ForgotPasswordGQL,
  ForgotUsernameGQL,
  UserHasSetPasswordGQL,
  ValidateUserGQL,
} from '@graphql/bifrost/generated';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Injectable({
  providedIn: 'root',
})
export class ResetPasswordService implements OnDestroy {
  private _subs = new SubSink();
  readonly resetPasswordStatus = signal<{
    type: 'error' | 'success' | 'info' | null;
    message: string;
  }>({
    type: null,
    message: '',
  });
  constructor(
    private readonly _forgetPasswordMutation: ForgotPasswordGQL,
    private readonly _changePasswordMutation: ChangeRequesterPasswordGQL,
    private readonly _validateUserQuery: ValidateUserGQL,
    private readonly _forgetUsernameMutation: ForgotUsernameGQL,
    private readonly _userHasSetPasswordQuery: UserHasSetPasswordGQL
  ) {}

  // Neutral confirmations shown for every request regardless of whether the email
  // matches an account — prevents enumeration per Faheem Hussain security review.
  private readonly _resetPasswordConfirmation =
    'If an account matches that email address, we have sent password reset instructions to it. ' +
    'Please check your inbox, and your spam folder if you do not see it shortly.';
  private readonly _requestUsernameConfirmation =
    'If an account matches that email address, we have sent the username to it. ' +
    'Please check your inbox, and your spam folder if you do not see it shortly.';

  requestForgetPassword(email: string) {
    return this._forgetPasswordMutation.mutate({ variables: { email } }).pipe(
      map(() => {
        this.resetPasswordStatus.set({
          type: 'success',
          message: this._resetPasswordConfirmation,
        });
      }),
      catchError((error) => {
        this.resetPasswordStatus.set({
          type: 'error',
          message: error,
        });
        return throwError(() => new Error(error));
      })
    );
  }
  requestForgetUsername(email: string) {
    return this._forgetUsernameMutation.mutate({ variables: { email } }).pipe(
      map(() => {
        this.resetPasswordStatus.set({
          type: 'success',
          message: this._requestUsernameConfirmation,
        });
      }),
      catchError(() => {
        this.resetPasswordStatus.set({
          type: 'error',
          message:
            'An error occurred while processing your request. Please try again.',
        });
        return of(null);
      })
    );
  }
  changePassword(newPassword: string, jwt: string, isFirstTime = false) {
    return this._changePasswordMutation
      .mutate({
        variables: { newPassword },
        context: {
          headers: new HttpHeaders({
            Authorization: `Bearer ${jwt}`,
          }),
        },
      })
      .pipe(
        map((res) => {
          if (!res.data?.changeRequesterPassword) {
            this.resetPasswordStatus.set({
              type: 'error',
              message: 'Error ! Password could not be updated.',
            });
          } else {
            let message =
              'Your password has been changed successfully. Please note this may take up to 3 minutes to take effect.';
            if (!isFirstTime) {
              message += ' Meanwhile your old password may continue to work.';
            }
            this.resetPasswordStatus.set({
              type: 'success',
              message,
            });
          }
        }),
        catchError((error) => {
          this.resetPasswordStatus.set({
            type: 'error',
            message: error.message,
          });
          return throwError(() => new Error(error));
        })
      );
  }
  validateUser(): Observable<boolean> {
    return this._validateUserQuery
      .fetch()
      .pipe(map((result) => result.data?.validateUser ?? false));
  }

  userHasSetPassword(email: string): Observable<boolean> {
    return this._userHasSetPasswordQuery
      .fetch({ variables: { email }, fetchPolicy: 'network-only' })
      .pipe(map((result) => result.data?.userHasSetPassword ?? true));
  }
  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
